import React from 'react';

/**
 * 根据钱包地址生成特征
 * @param {string} address 钱包地址
 * @returns {Object} 特征对象
 */
const generateFeatures = (address) => {
  if (!address) {
    // 默认特征
    return {
      backgroundColor: '#f9f3e5',
      bodyColor: '#5c4937',
      bodyType: 0,
      eyeType: 0,
      accessoryType: 0,
      hasBlush: false
    };
  }
  
  // 简单的哈希函数
  let hash = 0;
  for (let i = 0; i < address.length; i++) {
    hash = ((hash << 5) - hash) + address.charCodeAt(i);
    hash = hash & hash;
  }
  hash = Math.abs(hash);
  
  // 背景颜色 - 柔和的自然色调
  const bgHues = [45, 85, 160, 190, 210, 35]; // 多种柔和自然色调
  const bgHue = bgHues[hash % bgHues.length];
  const bgSat = 65 + (hash % 20);
  const bgLight = 80 + (hash % 15);
  
  // 身体颜色 - 自然色调
  const bodyHues = [30, 45, 60, 15, 350, 320, 170]; // 自然色调
  const bodyHue = bodyHues[(hash >> 4) % bodyHues.length];
  const bodySat = 40 + ((hash >> 8) % 30);
  const bodyLight = 30 + ((hash >> 12) % 40);
  
  // 特征类型
  const bodyType = hash % 3; // 不同形状的身体
  const eyeType = (hash >> 4) % 4; // 不同类型的眼睛
  const accessoryType = (hash >> 8) % 5; // 不同的装饰物
  const hasBlush = ((hash >> 12) % 2) === 1; // 是否有腮红
  
  return {
    backgroundColor: `hsl(${bgHue}, ${bgSat}%, ${bgLight}%)`,
    bodyColor: `hsl(${bodyHue}, ${bodySat}%, ${bodyLight}%)`,
    bodyType,
    eyeType,
    accessoryType,
    hasBlush
  };
};

/**
 * 钱包头像组件 - 吉卜力风格
 * 根据钱包地址生成一个独特的吉卜力风格头像
 */
export const WalletAvatar = ({ 
  address, 
  size = 40, 
  className = ''
}) => {
  const features = generateFeatures(address);
  
  // 根据大小计算内部尺寸和位置
  const eyeSize = Math.max(3, size / 12);
  const eyeSpacing = size / 6;
  
  return (
    <div 
      className={`relative rounded-full overflow-hidden ${className}`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: features.backgroundColor,
        boxShadow: `inset 0 0 ${size/10}px rgba(0,0,0,0.1)`
      }}
    >
      {/* 主体 */}
      <div 
        className="absolute"
        style={{
          width: `${size * 0.7}px`,
          height: `${size * 0.7}px`,
          backgroundColor: features.bodyColor,
          borderRadius: '50%',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: features.bodyType === 2 ? 0.85 : 1
        }}
      />
      
      {/* 多个可选装饰 - 根据bodyType生成不同的装饰 */}
      {features.bodyType === 1 && (
        <div className="absolute top-0 left-0 right-0 bottom-0">
          <div 
            className="absolute"
            style={{
              width: `${size * 0.85}px`,
              height: `${size * 0.3}px`,
              backgroundColor: features.bodyColor,
              borderRadius: '50%',
              top: '70%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(45deg)',
              opacity: 0.6
            }}
          />
          <div 
            className="absolute"
            style={{
              width: `${size * 0.85}px`,
              height: `${size * 0.3}px`,
              backgroundColor: features.bodyColor,
              borderRadius: '50%',
              top: '70%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              opacity: 0.6
            }}
          />
        </div>
      )}
      
      {features.bodyType === 2 && (
        <div className="absolute top-0 left-0 right-0 bottom-0">
          <div 
            className="absolute"
            style={{
              width: `${size * 0.85}px`,
              height: `${size * 0.85}px`,
              border: `${size/20}px solid ${features.bodyColor}`,
              borderRadius: '50%',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              opacity: 0.7
            }}
          />
        </div>
      )}
      
      {/* 眼睛 */}
      <div 
        className="absolute"
        style={{
          top: '42%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: `${eyeSpacing * 2 + eyeSize * 2}px`,
          height: `${eyeSize * 2}px`
        }}
      >
        {/* 左眼 */}
        <div 
          className="absolute"
          style={{
            width: `${eyeSize * 2}px`,
            height: features.eyeType === 3 ? `${eyeSize}px` : `${eyeSize * 2}px`,
            backgroundColor: features.eyeType === 2 ? 'transparent' : 'black',
            borderRadius: '50%',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            border: features.eyeType === 2 ? `${eyeSize/2}px solid black` : 'none'
          }}
        />
        
        {/* 右眼 */}
        <div 
          className="absolute"
          style={{
            width: `${eyeSize * 2}px`,
            height: features.eyeType === 3 ? `${eyeSize}px` : `${eyeSize * 2}px`,
            backgroundColor: features.eyeType === 2 ? 'transparent' : 'black',
            borderRadius: '50%',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            border: features.eyeType === 2 ? `${eyeSize/2}px solid black` : 'none'
          }}
        />
      </div>
      
      {/* 腮红 */}
      {features.hasBlush && (
        <>
          {/* 左腮红 */}
          <div 
            className="absolute"
            style={{
              width: `${size * 0.15}px`,
              height: `${size * 0.1}px`,
              backgroundColor: 'rgba(255, 150, 150, 0.6)',
              borderRadius: '50%',
              top: '55%',
              left: '25%',
              transform: 'translate(-50%, -50%)'
            }}
          />
          
          {/* 右腮红 */}
          <div 
            className="absolute"
            style={{
              width: `${size * 0.15}px`,
              height: `${size * 0.1}px`,
              backgroundColor: 'rgba(255, 150, 150, 0.6)',
              borderRadius: '50%',
              top: '55%',
              left: '75%',
              transform: 'translate(-50%, -50%)'
            }}
          />
        </>
      )}
      
      {/* 附加装饰 */}
      {features.accessoryType === 1 && (
        // 叶子装饰
        <div 
          className="absolute"
          style={{
            width: `${size * 0.25}px`,
            height: `${size * 0.25}px`,
            top: '0%',
            left: '60%',
            transform: 'rotate(45deg)'
          }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <path d="M50,0 Q80,40 50,100 Q20,40 50,0" fill="#7bb36b" />
          </svg>
        </div>
      )}
      
      {features.accessoryType === 2 && (
        // 小星星
        <div 
          className="absolute"
          style={{
            width: `${size * 0.2}px`,
            height: `${size * 0.2}px`,
            top: '15%',
            right: '15%'
          }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <path d="M50,0 L61,35 L97,35 L68,57 L79,91 L50,70 L21,91 L32,57 L3,35 L39,35 Z" fill="#f7d66c" />
          </svg>
        </div>
      )}
      
      {features.accessoryType === 3 && (
        // 小蘑菇
        <div 
          className="absolute"
          style={{
            width: `${size * 0.3}px`,
            height: `${size * 0.3}px`,
            bottom: '10%',
            right: '15%'
          }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <ellipse cx="50" cy="35" rx="40" ry="30" fill="#e06c6c" />
            <ellipse cx="50" cy="35" rx="10" ry="8" fill="white" opacity="0.5" />
            <rect x="40" y="35" width="20" height="50" fill="#d9b88f" />
          </svg>
        </div>
      )}
      
      {features.accessoryType === 4 && (
        // 小花
        <div 
          className="absolute"
          style={{
            width: `${size * 0.25}px`,
            height: `${size * 0.25}px`,
            top: '15%',
            left: '15%'
          }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle cx="50" cy="50" r="15" fill="#f7d06c" />
            <circle cx="20" cy="50" r="15" fill="#f7a5d3" />
            <circle cx="80" cy="50" r="15" fill="#f7a5d3" />
            <circle cx="50" cy="20" r="15" fill="#f7a5d3" />
            <circle cx="50" cy="80" r="15" fill="#f7a5d3" />
          </svg>
        </div>
      )}
    </div>
  );
};
